<template>
  <b-card class="jobs-list-item p-2" body-class="px-3" footer-class="bg-primary-a1 border-0 rounded px-3">
    <div class="jobs-list-header d-flex flex-row justify-content-start align-items-start pb-4">
      <h3 class="flex-fill m-0">
        <a v-if="!isLoggedIn && job.atsURL" :href="job.atsURL">{{ job.title }}</a>
        <router-link v-else :to="toJob">{{ job.title }}</router-link>
      </h3>
      <div class="flex-fill ml-3 ml-sm-1 pt-2 text-uppercase text-right" v-if="job.visited && job.visited.visits > 0">
        {{ $t('jobs.status.viewed') }}
      </div>
    </div>

    <key-stats :job="job" v-if="job"/>

    <div class="d-none d-sm-block mt-3" v-if="job.description && !hideJobExcerpts">
      <p v-if="job.excerpt" class="text-secondary-a8">{{ job.excerpt }}</p>
      <p class="text-secondary-a8">
        <a v-if="!isLoggedIn && job.atsURL" :href="job.atsURL">Read more</a>
        <router-link v-else :to="{ ...toJob, hash: '#description' }">Read more</router-link>
      </p>
    </div>

    <template v-slot:footer v-if="isLoggedIn">
      <div class="d-flex flex-row justify-content-between align-items-center" v-if="canSyncContacts">
        <h5 class="flex-fill text-left">
          <template v-if="isRecruiter && hasMatches">{{ $t('jobs.ctas.view-matched-candidates') }}</template>
          <template v-else-if="isRecruiter">{{ $t('jobs.ctas.search-candidates') }}</template>
          <template v-else-if="hasMatches">
            <template v-if="job.matchesTotal.total > 20">{{ $t('jobs.ctas.more-than-n-matches', { n: 20 }) }}</template>
            <template v-else>{{ $tc('jobs.ctas.has-n-matches', job.matchesTotal.total) }}</template>
          </template>
          <template v-else>&nbsp;</template>
        </h5>
        <b-btn v-if="showLinkedInSearchOnJobs" class="mr-3" variant="linkedin" v-b-modal="`job-linkedinSearch-${job.id}`">
          <font-awesome-icon class="mr-2" :icon="['fab', 'linkedin']"/> Matches</b-btn>
        <b-btn :to="toJob" variant="primary" data-tour="refer-button">
          <template v-if="isRecruiter">{{ $t('jobs.ctas.item-recruiter') }}</template>
          <template v-else>{{ $t('jobs.ctas.item-employee') }}</template>
        </b-btn>
      </div>
      <div class="d-flex flex-row justify-content-end align-items-center" v-else>
        <b-btn class="mr-3" :to="toJob" variant="outline-primary">{{ $t('jobs.ctas.view-job') }}</b-btn>
        <b-btn v-if="showLinkedInSearchOnJobs" class="mr-3" variant="linkedin" v-b-modal="`job-linkedinSearch-${job.id}`">
          <font-awesome-icon class="mr-2" :icon="['fab', 'linkedin']"/> Matches</b-btn>
        <template v-if="job.status === 'ACTIVE'">
          <b-btn v-if="isNativeApp" @click.prevent="triggerNativeShare" variant="primary">
            <font-awesome-icon icon="share"/> {{ $t('jobs.ctas.share-now') }}
          </b-btn>
          <b-btn v-else v-b-modal="`share-${job.id}`" variant="primary">
            <font-awesome-icon icon="share"/> {{ $t('jobs.ctas.share-now') }}
          </b-btn>
        </template>
      </div>
      <b-modal v-if="showLinkedInSearchOnJobs" :id="`job-linkedinSearch-${job.id}`" title="Search LinkedIn" hide-footer lazy>
        <linkedin-search-details :jobID="job.id" :jobTitle="job.title" :shareUrl="shareUrl"
          :linkedInSearchURL="(job.customFieldsHash ? job.customFieldsHash.linkedInSearchURL : null) || job.linkedInSearchURL"
          @trackedLiBrowseEvent="trackedLiBrowseEvent"
          @trackShareEvent="trackShareEvent" />
      </b-modal>
    </template>
    <template v-slot:footer v-else-if="!isLoggedIn">
      <div class="d-flex flex-row justify-content-end align-items-center">
        <b-btn v-if="job.atsURL" :href="job.atsURL" variant="primary">{{ $t('ctas.read-more') }}</b-btn>
        <b-btn v-else :to="toJob" variant="primary">{{ $t('ctas.read-more') }}</b-btn>
      </div>
    </template>

    <b-modal :id="`share-${job.id}`" title="Share Job" hide-header hide-footer lazy>
      <share class="w-100" title="Share This Role" :url="shareUrl" show-preview :preview-args="{ title: job.title }"
        @modal-close="() => $bvModal.hide(`share-${job.id}`)" @track="trackShareEvent">
          <template v-slot:text>
            <p>{{ $t('jobs.share-modal.description') }}</p>
          </template>
      </share>
    </b-modal>

  </b-card>

</template>

<script>
import keyStats from '@/components/jobs/key-stats';
import LinkedinSearchDetails from '@src/components/jobs/LinkedInSearch';
import { mapRoot, mapPreferences } from '@src/mixins/root';
import Share, { triggerNativeShare } from '@/components/Share';

export default {
  props: {
    // Pass through a Job object, hopefully from the API!
    job: {
      type: Object,
      required: true,
      validate(job) {
        if (job.__typename && job.__typename === 'Job') {
          // If this is from our API, with a relevant typename, allow it through
          return true;
        } else {
          return job && job.id;
        }
      },
    },

    layout: {
      type: String,
      default: () => 'block',
      validate: layout => [ 'block', 'page', 'select-modal' ].includes(layout),
    },

    // Pass through a variable to control how many matches the item should render
    // Set this to 0 to hide all related match data from the item
    // maximumMatches: {
    //   type: Number,
    //   default: 5,
    // },
  },
  components: {
    keyStats,
    Share,
    LinkedinSearchDetails,
  },
  methods: {
    triggerNativeShare() {
      triggerNativeShare(this.shareUrl, () => this.trackShareEvent({ channel: 'native' }));
    },
    async trackedLiBrowseEvent() {
      try {
        const { id: jobID } = this.job;
        await this.$root.captureLog({
          eventName: 'JOB_BROWSE_LINKEDIN',
          job: { id: jobID },
          user: { self: true },
        });
      } catch (err) {
        this.$log.error(err);
      }
    },
    trackShareEvent({ channel }) {
      const { shareCode } = this.job;
      Promise.resolve(this.$root.captureLog({
        eventName: 'JOB_SHARED',
        job: { share: shareCode },
        user: { share: shareCode },
        referral: { share: shareCode },
        data: channel ? [ { key: 'channel', value: channel } ] : null,
      }));
    },
  },
  computed: {
    ...mapRoot([ 'company', 'isNativeApp', 'isLoggedIn', 'isRecruiter', 'isCommunitySite', 'isSainsburys' ]),
    ...mapPreferences([ 'canSyncContacts', 'showLinkedInSearchOnJobs' ]),
    hasMatches() {
      const { matchesTotal } = this.job || {};
      return matchesTotal && matchesTotal.total > 0;
    },
    shareUrl() {
      const { origin } = this.$root;
      return `${origin}/r/${this.job.shareCode}`;
    },
    hideJobExcerpts() {
      const { sitePreferences: { hideJobExcerpts = false } = {} } = this.company;
      return hideJobExcerpts === true;
    },
    toJob() {
      const { job: { id }, $route: { name, params: { shareCode } } } = this;
      if (`${name}`.startsWith('ShareShow') && shareCode) {
        return {
          name: 'ShareShowAnother',
          params: { shareCode, jobCode: id },
        };
      } else {
        return {
          name: 'JobShow',
          params: { jobID: id },
        };
      }

    },
    toCandidates() {
      return {
        ...this.toJob,
        hash: '#candidates',
      };
    },
  },
};
</script>

<style lang="scss">
.jobs-list-item {

  p {
    margin: 0;
  }

  .contact-picture {
    display: inline-block;
  }

  .jobs-list-header {
    h3 {
      a {
        &, &:hover {
          color: inherit;
          font-weight: inherit;
          text-decoration: none;
        }
      }
    }

    .label {
      border: 1px solid transparent;
      border-radius: 4px;
      line-height: 1.5;
      padding: 0.375rem;
      text-align: center;
      vertical-align: middle;
    }
  }

  .job-image {
    display: block;
    max-width: 150px;
    max-height: 150px;
    width: auto;
    height: auto;
  }

  .jobs-list-row {
    padding-bottom: 20px;
  }

  .btn-primary, .btn-outline-primary {
    border-radius: 4px;
    font-size: 15px;
  }

  .card-footer {
    padding: 10px;

    .row {
      margin-left: 0px;
      margin-right: 0px;
    }

    .btn-primary, .btn-outline-primary {
      border-radius: 4px;
      font-size: 15px;
    }

    h5 {
      line-height: 25px;
      font-size: 15px;
      margin: 0;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/jobs/JobsListItem.vue